// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording
// invented for this application; it contains no Qur'an, hadith, translation or religious quotation.

import { messages, useMessages } from '../i18n.js';

/** Ordinary, synthetic everyday-life starters. Two at most on the home screen (redesign canvas). */
const T = messages({
  ar: {
    greetingAnonymous: 'مرحباً، أنا بلال',
    greetingNamed: (name: string) => `مرحباً ${name}،`,
    greetingSecond: 'كيف أساعدك اليوم؟',
    explore: 'استكشف المصادر الموثقة',
    examples: ['أشعر بضغط في العمل ولا أعرف من أين أبدأ.', 'أريد أن أرتّب يومي بشكل أفضل.'],
  },
  en: {
    greetingAnonymous: "Hello, I'm Bilal",
    greetingNamed: (name: string) => `Hello ${name},`,
    greetingSecond: 'How can I help today?',
    explore: 'Explore the verified sources',
    examples: [
      'Work is weighing on me and I do not know where to start.',
      'I want to order my day better.',
    ],
  },
});

/**
 * The empty conversation, as the redesign draws it: a two-tone greeting above the composer — the
 * first line light, the second carrying the weight — and at most two starters beneath. Two
 * components so the composer can sit between them while DOM order stays keyboard order (the
 * greeting holds nothing tabbable).
 */
export function EmptyGreeting({
  name = null,
}: {
  readonly name?: string | null;
}): React.JSX.Element {
  const t = useMessages(T);
  return (
    <div className="empty-greeting">
      <h2>
        {/* By name once the person has stated one (ADR-034); the canvas greets "مرحباً سلطان،". */}
        <span className="greet-line-1">
          {name === null ? t.greetingAnonymous : t.greetingNamed(name)}
        </span>
        <span className="greet-line-2">{t.greetingSecond}</span>
      </h2>
    </div>
  );
}

export function EmptyExamples({
  onPick,
  onExplore,
}: {
  readonly onPick: (text: string) => void;
  /** Opens the verified-sources Explore surface; the chip advertises it where Qaf advertises its typed powers. */
  readonly onExplore?: (() => void) | undefined;
}): React.JSX.Element {
  const t = useMessages(T);
  return (
    <div className="empty-examples">
      <div className="examples">
        {t.examples.map((example) => (
          <button
            key={example}
            type="button"
            onClick={() => {
              onPick(example);
            }}
          >
            {example}
          </button>
        ))}
        {onExplore !== undefined && (
          <button type="button" className="explore-chip" onClick={onExplore}>
            {t.explore}
          </button>
        )}
      </div>
    </div>
  );
}
